export const neighborhoods = [
  // ── San Francisco ────────────────────────────────────────────────────────────
  {
    name: 'Pacific Heights',
    slug: 'pacific-heights',
    region: 'sf',
    tagline: 'Iconic mansions, bay views, and upscale city living',
    description: "Pacific Heights is one of San Francisco's most prestigious and sought-after neighborhoods. Perched atop a ridge with sweeping views of the bay, Marin, and the Golden Gate, it's defined by grand Victorian and Edwardian mansions, manicured streets, and a quiet, residential feel that's rare in a city this dense. Fillmore Street anchors the neighborhood's social life — a walkable stretch of boutiques, wine bars, and beloved cafés that draw locals and visitors alike. Families appreciate the top-rated schools and proximity to parks. Professionals are drawn by the elegance and relative calm. Pacific Heights consistently ranks among the highest-priced zip codes in the country, and demand has remained strong across all market cycles.",
    vibe: ['Upscale', 'Quiet', 'Family-Friendly', 'Walkable'],
    image: 'https://images.unsplash.com/photo-1754254974632-b882835cb34a?auto=format&fit=crop&w=1200&q=80',
    market: {
      medianPrice: '$8.5M',
      pricePerSqft: '$1,250',
      avgDaysOnMarket: 90,
      updated: 'March 2026',
    },
    scores: { walk: 96, bike: 78, transit: 80, school: 85 },
    demographics: {
      population: '22,500',
      medianAge: 38,
      medianIncome: '$156K',
      medianRent: '$3,450',
    },
    schools: [
      { name: 'Hamlin School', grades: 'K–8', type: 'Private', rating: 9 },
      { name: 'Convent of the Sacred Heart', grades: 'K–12', type: 'Private', rating: 9 },
      { name: 'McKinley Elementary', grades: 'K–5', type: 'Public', rating: 8 },
    ],
    restaurants: [
      { name: 'State Bird Provisions', type: 'Californian', note: 'James Beard Award-winning dim sum-style fine dining' },
      { name: 'The Shell', type: 'Seafood', note: 'Intimate seafood bar by the team behind State Bird' },
      { name: 'Dosa', type: 'Indian', note: 'South Indian cuisine with an acclaimed wine program' },
      { name: 'Fillmore Bakeshop', type: 'Café', note: 'Beloved neighborhood bakery and coffee spot on Fillmore' },
    ],
    thingsToDo: [
      { name: 'Alta Plaza Park', type: 'Park', note: 'Hilltop park with panoramic city views and popular dog runs' },
      { name: 'Fillmore Street', type: 'Shopping', note: 'Upscale boutiques, wine shops, and galleries' },
      { name: 'Lafayette Park', type: 'Park', note: 'Tree-lined square perfect for picnics and weekend strolls' },
      { name: 'Broadway Mansion Row', type: 'Sightseeing', note: "Walk past some of the city's most spectacular Victorian estates" },
    ],
  },
  {
    name: 'Marina District',
    slug: 'marina',
    region: 'sf',
    tagline: 'Waterfront living, active lifestyle, and a vibrant social scene',
    description: "The Marina is where San Francisco's young professionals, active locals, and weekend brunch crowd converge. Built on landfill after the 1906 earthquake, the neighborhood is characterized by its Mediterranean-style stucco buildings, flat streets ideal for jogging and cycling, and proximity to the waterfront. Chestnut Street is the neighborhood's main artery — packed with coffee shops, wine bars, restaurants, and boutiques. The Palace of Fine Arts provides one of the most iconic backdrops in the city. Crissy Field offers stunning Golden Gate views and miles of shoreline trails. The Marina has a decidedly social, energetic character that appeals to those who want the best of city life with a slightly more relaxed pace.",
    vibe: ['Active', 'Social', 'Young Professional', 'Walkable'],
    image: 'https://images.unsplash.com/photo-1610494475096-ad11c3a21638?auto=format&fit=crop&w=1200&q=80',
    market: {
      medianPrice: '$5.3M',
      pricePerSqft: '$1,100',
      avgDaysOnMarket: 15,
      updated: 'March 2026',
    },
    scores: { walk: 97, bike: 84, transit: 73, school: 72 },
    demographics: {
      population: '15,300',
      medianAge: 33,
      medianIncome: '$138K',
      medianRent: '$3,200',
    },
    schools: [
      { name: 'Marina Middle School', grades: '6–8', type: 'Public', rating: 7 },
      { name: 'San Francisco Day School', grades: 'K–8', type: 'Private', rating: 9 },
      { name: 'Sherman Elementary', grades: 'K–5', type: 'Public', rating: 8 },
    ],
    restaurants: [
      { name: 'Delarosa', type: 'Italian', note: 'Roman-style thin-crust pizza and aperitivo cocktails' },
      { name: 'Roam Artisan Burgers', type: 'American', note: 'Grass-fed burgers with local, sustainable ingredients' },
      { name: 'Causwells', type: 'American', note: 'Classic American fare with a strong cocktail program' },
      { name: 'Boon Fly Café', type: 'Café', note: 'Relaxed neighborhood brunch spot with great coffee' },
    ],
    thingsToDo: [
      { name: 'Palace of Fine Arts', type: 'Landmark', note: 'Stunning Greco-Roman rotunda and reflection lagoon' },
      { name: 'Crissy Field', type: 'Outdoors', note: 'Restored wetlands and shoreline path with Golden Gate views' },
      { name: 'Chestnut Street', type: 'Shopping', note: 'Boutiques, fitness studios, and a lively restaurant row' },
      { name: 'Fort Mason', type: 'Culture', note: 'Former military post turned cultural and event hub' },
    ],
  },
  {
    name: 'Noe Valley',
    slug: 'noe-valley',
    region: 'sf',
    tagline: 'Sunny, family-friendly, and full of neighborhood charm',
    description: "Noe Valley is one of San Francisco's sunniest neighborhoods thanks to its position south of Twin Peaks, which blocks much of the summer fog. That sunshine is a huge draw, but what keeps people here is the neighborhood's genuine warmth and community feel. 24th Street is the heartbeat — a walkable stretch of independent coffee shops, bookstores, children's boutiques, and farm-to-table restaurants that feel a world away from downtown. Victorian and Edwardian homes line the quiet side streets. Young families are the dominant demographic, drawn by top elementary schools, stroller-friendly sidewalks, and a slower pace of life. Noe Valley consistently ranks among the most livable neighborhoods in the city.",
    vibe: ['Family-Friendly', 'Sunny', 'Walkable', 'Community-Oriented'],
    image: 'https://images.unsplash.com/photo-1742559398410-8332a8d3d140?auto=format&fit=crop&w=1200&q=80',
    market: {
      medianPrice: '$3.0M',
      pricePerSqft: '$1,150',
      avgDaysOnMarket: 8,
      updated: 'March 2026',
    },
    scores: { walk: 96, bike: 72, transit: 72, school: 80 },
    demographics: {
      population: '25,400',
      medianAge: 37,
      medianIncome: '$162K',
      medianRent: '$3,350',
    },
    schools: [
      { name: 'Alvarado Elementary', grades: 'K–5', type: 'Public', rating: 8 },
      { name: 'James Lick Middle School', grades: '6–8', type: 'Public', rating: 6 },
      { name: 'SF Friends School', grades: 'K–8', type: 'Private', rating: 9 },
    ],
    restaurants: [
      { name: 'Contigo', type: 'Spanish', note: 'Barcelona-inspired tapas with a cozy courtyard' },
      { name: 'Forage SF', type: 'Californian', note: 'Community-focused café with seasonal, local ingredients' },
      { name: 'Batter Bakery', type: 'Bakery', note: 'Beloved neighborhood spot known for creative pastries and cakes' },
      { name: 'Noe Valley Bakery', type: 'Bakery', note: 'Classic neighborhood bakery on 24th St, a local institution' },
    ],
    thingsToDo: [
      { name: '24th Street', type: 'Shopping', note: 'Independent shops, bookstores, and community staples' },
      { name: 'Noe Valley Town Square', type: 'Park', note: 'Community gathering space with farmers market on Saturdays' },
      { name: 'Sanchez Street Stairs', type: 'Outdoors', note: 'Scenic stairway climb with city and bay views' },
      { name: 'Douglass Dog Park', type: 'Park', note: 'Hilltop park with off-leash area and panoramic views' },
    ],
  },
  {
    name: 'The Castro',
    slug: 'castro',
    region: 'sf',
    tagline: 'A historic, vibrant neighborhood with an iconic spirit',
    description: "The Castro is one of the world's most famous neighborhoods — a symbol of LGBTQ+ history, community resilience, and San Francisco's culture of acceptance. The neighborhood centers on the intersection of Castro and 18th Street, where rainbow flags line the streets and the iconic Castro Theatre anchors the block. Beyond its cultural significance, the Castro is simply a great neighborhood to live in: beautiful Victorian architecture, excellent restaurants and bars, and a lively commercial corridor. Twin Peaks provides a natural southern boundary, and the hilltop views from Corona Heights Park are some of the best in the city. The Castro continues to attract a diverse mix of longtime residents, young professionals, and newcomers.",
    vibe: ['Vibrant', 'Historic', 'Diverse', 'Walkable'],
    image: 'https://images.unsplash.com/photo-1776004517628-e8982a3b27bc?auto=format&fit=crop&w=1200&q=80',
    market: {
      medianPrice: '$1.5M',
      pricePerSqft: '$950',
      avgDaysOnMarket: 22,
      updated: 'March 2026',
    },
    scores: { walk: 98, bike: 86, transit: 79, school: 68 },
    demographics: {
      population: '19,200',
      medianAge: 37,
      medianIncome: '$121K',
      medianRent: '$2,950',
    },
    schools: [
      { name: 'Harvey Milk Civil Rights Academy', grades: 'K–5', type: 'Public', rating: 6 },
      { name: 'Sanchez Elementary', grades: 'K–5', type: 'Public', rating: 6 },
      { name: 'SF University High School', grades: '9–12', type: 'Private', rating: 9 },
    ],
    restaurants: [
      { name: 'Anchor Oyster Bar', type: 'Seafood', note: 'A Castro institution since 1977 — fresh oysters and clam chowder' },
      { name: 'Saru Sushi Bar', type: 'Japanese', note: 'Omakase-style sushi in an intimate, minimalist setting' },
      { name: 'Poesia', type: 'Italian', note: 'Cozy Italian trattoria with handmade pasta and a great wine list' },
      { name: 'Café Flore', type: 'Café', note: 'Classic Castro café with sunny patio, open since 1973' },
    ],
    thingsToDo: [
      { name: 'Castro Theatre', type: 'Culture', note: 'Iconic 1922 movie palace hosting films, festivals, and events' },
      { name: 'Corona Heights Park', type: 'Outdoors', note: 'Rocky hilltop park with sweeping views of the city skyline' },
      { name: 'GLBT History Museum', type: 'Culture', note: "America's first LGBTQ history museum" },
      { name: 'Harvey Milk Plaza', type: 'Landmark', note: 'A tribute to civil rights icon Harvey Milk at the Castro Muni stop' },
    ],
  },
  {
    name: 'The Mission',
    slug: 'mission',
    region: 'sf',
    tagline: 'Latino roots, world-class food, and a creative, eclectic energy',
    description: "The Mission District is the cultural heart of San Francisco — a dense, flat, and almost always sunny neighborhood that's home to the city's best and most diverse dining scene, vibrant street art, and a creative community that has persisted through decades of change. Established as a Latino neighborhood, the Mission still retains much of that character alongside an influx of tech workers, artists, and young professionals. Valencia Street offers a parade of trendy restaurants and boutiques. Mission Street is rawer, more authentic, and home to taquerias, panaderías, and tiendas that have been there for generations. Dolores Park is one of the great urban parks in America — a people-watching paradise on any given weekend.",
    vibe: ['Eclectic', 'Sunny', 'Foodie', 'Artistic'],
    image: 'https://images.unsplash.com/photo-1503449539626-3ca98a08ad3c?auto=format&fit=crop&w=1200&q=80',
    market: {
      medianPrice: '$1.5M',
      pricePerSqft: '$900',
      avgDaysOnMarket: 9,
      updated: 'March 2026',
    },
    scores: { walk: 98, bike: 88, transit: 79, school: 62 },
    demographics: {
      population: '46,700',
      medianAge: 33,
      medianIncome: '$95K',
      medianRent: '$2,700',
    },
    schools: [
      { name: 'Mission Dolores Academy', grades: 'K–8', type: 'Catholic', rating: 7 },
      { name: 'Cesar Chavez Elementary', grades: 'K–5', type: 'Public', rating: 4 },
      { name: 'Horace Mann Middle School', grades: '6–8', type: 'Public', rating: 5 },
    ],
    restaurants: [
      { name: 'Tartine Manufactory', type: 'Bakery / Café', note: "San Francisco's most celebrated bakery — arrive early for croissants" },
      { name: 'La Taqueria', type: 'Mexican', note: 'James Beard Award-winning tacos and burritos since 1973' },
      { name: 'Flour + Water', type: 'Italian', note: 'Acclaimed handmade pasta and Neapolitan pizza on Valencia' },
      { name: 'Dandelion Chocolate', type: 'Café', note: 'Bean-to-bar chocolate factory and café on Valencia Street' },
    ],
    thingsToDo: [
      { name: 'Dolores Park', type: 'Park', note: 'The city\'s favorite outdoor living room with skyline views' },
      { name: 'Balmy Alley', type: 'Culture', note: 'World-famous mural alley showcasing decades of political street art' },
      { name: 'Valencia Street', type: 'Shopping', note: 'Independent bookstores, vintage shops, and restaurant corridor' },
      { name: 'Mission Dolores', type: 'Landmark', note: "San Francisco's oldest building, founded in 1776" },
    ],
  },
  {
    name: 'Hayes Valley',
    slug: 'hayes-valley',
    region: 'sf',
    tagline: 'Boutique, artistic, and perfectly positioned in the heart of the city',
    description: "Hayes Valley underwent a remarkable transformation after the 1989 earthquake led to the demolition of an elevated freeway that once divided the neighborhood. What emerged was a walkable, design-forward district that's become one of the most coveted addresses in the city. The neighborhood is compact but remarkably complete — great coffee, acclaimed restaurants, independent fashion boutiques, and easy access to the performing arts at the nearby Davies Symphony Hall and SF Opera. Patricia's Green provides a small but beloved community gathering space. Hayes Valley attracts design professionals, musicians, and anyone who appreciates a neighborhood with genuine urban character and walkability.",
    vibe: ['Artistic', 'Walkable', 'Design-Forward', 'Central'],
    image: 'https://images.unsplash.com/photo-1745817137606-0213e9e88df6?auto=format&fit=crop&w=1200&q=80',
    market: {
      medianPrice: '$887K',
      pricePerSqft: '$1,000',
      avgDaysOnMarket: 23,
      updated: 'March 2026',
    },
    scores: { walk: 98, bike: 91, transit: 84, school: 66 },
    demographics: {
      population: '16,800',
      medianAge: 35,
      medianIncome: '$118K',
      medianRent: '$3,100',
    },
    schools: [
      { name: 'Dianne Feinstein Elementary', grades: 'K–5', type: 'Public', rating: 7 },
      { name: 'Buena Vista Horace Mann', grades: 'K–8', type: 'Public', rating: 7 },
      { name: 'Gateway High School', grades: '9–12', type: 'Charter', rating: 7 },
    ],
    restaurants: [
      { name: 'Rich Table', type: 'Californian', note: 'Michelin-starred neighborhood restaurant known for creative, seasonal cuisine' },
      { name: 'Souvla', type: 'Greek', note: 'Fast-casual Greek with rotisserie meats and excellent wine' },
      { name: 'Smitten Ice Cream', type: 'Dessert', note: 'Liquid nitrogen ice cream made to order with local ingredients' },
      { name: 'Ritual Coffee', type: 'Café', note: 'One of SF\'s top specialty coffee roasters, with a Hayes Valley flagship' },
    ],
    thingsToDo: [
      { name: "Patricia's Green", type: 'Park', note: 'Community green space with rotating public art installations' },
      { name: 'SF Jazz Center', type: 'Culture', note: 'World-class jazz venue one block from the neighborhood' },
      { name: 'Davies Symphony Hall', type: 'Culture', note: 'Home of the San Francisco Symphony' },
      { name: 'Hayes Street Shopping', type: 'Shopping', note: 'Independent boutiques from local and emerging designers' },
    ],
  },
  {
    name: 'Russian Hill',
    slug: 'russian-hill',
    region: 'sf',
    tagline: 'Steep streets, stunning views, and serene residential character',
    description: "Russian Hill is one of San Francisco's most topographically dramatic neighborhoods — a steep rise between North Beach and Polk Gulch with views that reach across the bay to Marin and Alcatraz. The neighborhood is best known to visitors for Lombard Street's famous crooked block, but for residents it offers something quieter and more lasting: a genuinely peaceful urban retreat. The streets are narrow and hilly, the Victorians and Edwardians are beautifully maintained, and the overall character is more contemplative than social. Polk Street on the western edge provides grocery stores, cafés, and restaurants. The neighborhood has long attracted writers and artists, and that creative, thoughtful energy persists today.",
    vibe: ['Quiet', 'Scenic', 'Residential', 'Historic'],
    image: 'https://images.unsplash.com/photo-1710070215593-f200b20abef5?auto=format&fit=crop&w=1200&q=80',
    market: {
      medianPrice: '$4.1M',
      pricePerSqft: '$1,200',
      avgDaysOnMarket: 24,
      updated: 'March 2026',
    },
    scores: { walk: 96, bike: 60, transit: 78, school: 74 },
    demographics: {
      population: '14,500',
      medianAge: 37,
      medianIncome: '$135K',
      medianRent: '$3,150',
    },
    schools: [
      { name: 'Francisco Middle School', grades: '6–8', type: 'Public', rating: 6 },
      { name: 'Spring Valley Elementary', grades: 'K–5', type: 'Public', rating: 7 },
      { name: 'SF University High School', grades: '9–12', type: 'Private', rating: 9 },
    ],
    restaurants: [
      { name: 'Zarzuela', type: 'Spanish', note: 'Tapas and paella in a cozy, long-running neighborhood institution' },
      { name: 'La Folie', type: 'French', note: 'Classic fine French dining on Polk Street' },
      { name: 'Swensen\'s Ice Cream', type: 'Dessert', note: 'The original location of the beloved ice cream chain, since 1948' },
      { name: 'Macchiarini Creative Design', type: 'Café', note: 'Legendary Italian café and coffee roaster on upper Polk' },
    ],
    thingsToDo: [
      { name: 'Lombard Street', type: 'Landmark', note: "The world's most crooked street — an iconic SF experience" },
      { name: 'Ina Coolbrith Park', type: 'Park', note: 'Hidden hilltop park with spectacular views across the bay' },
      { name: 'Macondray Lane', type: 'Sightseeing', note: 'A peaceful wooden boardwalk through lush gardens — the real Barbary Lane' },
      { name: 'Polk Street', type: 'Shopping', note: 'Local bars, restaurants, antique shops, and boutiques' },
    ],
  },
  {
    name: 'Nob Hill',
    slug: 'nob-hill',
    region: 'sf',
    tagline: 'Old-world grandeur, historic hotels, and sweeping city views',
    description: "Nob Hill is San Francisco's most storied hilltop — home to the city's first generation of railroad and silver barons, whose mansions gave the neighborhood its name. Today, it's defined by grand historic hotels (the Fairmont, Huntington, and Mark Hopkins), Grace Cathedral, and Huntington Park, all clustered around California and Mason streets at the city's highest residential point. The housing stock is primarily condos and apartments in stately buildings, which attracts a mix of long-term residents and executives who value proximity to downtown with an elevated remove from the energy of the streets below. The views — east to the Bay Bridge and Oakland, west to the Richmond — are exceptional.",
    vibe: ['Elegant', 'Quiet', 'Historic', 'Central'],
    image: 'https://images.unsplash.com/photo-1507603312191-2b1e247ef6b3?auto=format&fit=crop&w=1200&q=80',
    market: {
      medianPrice: '$1.5M',
      pricePerSqft: '$900',
      avgDaysOnMarket: 36,
      updated: 'March 2026',
    },
    scores: { walk: 97, bike: 63, transit: 87, school: 70 },
    demographics: {
      population: '20,100',
      medianAge: 38,
      medianIncome: '$122K',
      medianRent: '$2,900',
    },
    schools: [
      { name: 'Convent of the Sacred Heart', grades: 'K–12', type: 'Private', rating: 9 },
      { name: 'Garfield Elementary', grades: 'K–5', type: 'Public', rating: 6 },
      { name: 'Galileo High School', grades: '9–12', type: 'Public', rating: 6 },
    ],
    restaurants: [
      { name: 'Acquerello', type: 'Italian', note: 'Celebrated fine-dining Italian in a converted chapel' },
      { name: 'Tonga Room', type: 'Tiki Bar', note: "Fairmont Hotel's legendary tiki bar with indoor rainstorms since 1945" },
      { name: 'Big 4 Restaurant', type: 'American', note: 'Classic steakhouse in the Huntington Hotel, a power-lunch institution' },
      { name: 'Café Mozart', type: 'Café', note: 'Quiet European café with pastries and light bites' },
    ],
    thingsToDo: [
      { name: 'Grace Cathedral', type: 'Landmark', note: "SF's stunning Gothic cathedral with a labyrinth and Ghiberti doors" },
      { name: 'Huntington Park', type: 'Park', note: 'Elegant square at the top of the hill with bay views' },
      { name: 'Cable Car Ride', type: 'Experience', note: 'The Powell-Hyde line climbs directly through Nob Hill' },
      { name: 'Fairmont Hotel', type: 'Landmark', note: 'Grand 1907 hotel — worth a visit to see the lobby alone' },
    ],
  },
  {
    name: 'Inner Richmond',
    slug: 'inner-richmond',
    region: 'sf',
    tagline: 'A diverse, food-rich neighborhood bordering Golden Gate Park',
    description: "The Inner Richmond is one of San Francisco's most livable and underrated neighborhoods — a genuinely diverse, food-obsessed community that borders the eastern end of Golden Gate Park. Clement Street, the neighborhood's main commercial artery, is often called the 'second Chinatown' for its density of authentic Chinese, Vietnamese, and Russian restaurants, but it's also home to bookstores, bars, and beloved local institutions. The housing stock is solid and varied, with flats, Victorians, and small apartment buildings on tree-lined streets. Proximity to the park — with its museums, gardens, and miles of paths — is a major draw for families and outdoor enthusiasts.",
    vibe: ['Diverse', 'Foodie', 'Family-Friendly', 'Quiet'],
    image: 'https://images.unsplash.com/photo-1659037607675-2260ca77d47f?auto=format&fit=crop&w=1200&q=80',
    market: {
      medianPrice: '$3.4M',
      pricePerSqft: '$950',
      avgDaysOnMarket: 13,
      updated: 'March 2026',
    },
    scores: { walk: 95, bike: 76, transit: 71, school: 82 },
    demographics: {
      population: '33,200',
      medianAge: 38,
      medianIncome: '$112K',
      medianRent: '$2,850',
    },
    schools: [
      { name: 'George Peabody Elementary', grades: 'K–5', type: 'Public', rating: 8 },
      { name: 'Presidio Middle School', grades: '6–8', type: 'Public', rating: 7 },
      { name: 'Sacred Heart Cathedral Prep', grades: '9–12', type: 'Private', rating: 8 },
    ],
    restaurants: [
      { name: 'Hong Kong Lounge II', type: 'Chinese', note: 'Outstanding dim sum in a formal Hong Kong-style setting' },
      { name: 'Burma Superstar', type: 'Burmese', note: 'Long lines for a reason — the tea leaf salad is legendary' },
      { name: 'Nitan Ramen', type: 'Japanese', note: 'Refined ramen with house-made noodles on Geary Blvd' },
      { name: 'Green Apple Books', type: 'Café', note: 'SF institution — bookstore and café on Clement St since 1967' },
    ],
    thingsToDo: [
      { name: 'Golden Gate Park', type: 'Outdoors', note: "SF's 1,017-acre backyard — museums, gardens, trails, and lakes" },
      { name: 'de Young Museum', type: 'Culture', note: 'World-class fine arts museum inside the park' },
      { name: 'Clement Street', type: 'Shopping', note: "SF's most diverse food street with shops, cafés, and markets" },
      { name: 'Sutro Baths', type: 'Outdoors', note: 'Ruins of a historic Victorian bathhouse at Land\'s End' },
    ],
  },
  {
    name: 'Inner Sunset',
    slug: 'inner-sunset',
    region: 'sf',
    tagline: 'A laid-back, local neighborhood with great food and park access',
    description: "The Inner Sunset is anchored by 9th Avenue and Irving Street — a compact, low-key commercial corridor full of independent restaurants, coffee shops, and local businesses that have been there for decades. It's a neighborhood that rewards those who are drawn to authenticity over trendiness. The UCSF Parnassus campus brings a steady presence of students, researchers, and medical professionals, giving the neighborhood an intellectual, slightly bohemian character. Golden Gate Park forms the northern border, and Sutro Tower looms above. The N-Judah Muni line connects to downtown in under 20 minutes. Housing is primarily flats and Edwardian homes, priced below the city median for the quality offered.",
    vibe: ['Laid-Back', 'Local', 'Intellectual', 'Walkable'],
    image: 'https://images.unsplash.com/photo-1776004517835-5a8a80cb7364?auto=format&fit=crop&w=1200&q=80',
    market: {
      medianPrice: '$2.3M',
      pricePerSqft: '$1,000',
      avgDaysOnMarket: 32,
      updated: 'March 2026',
    },
    scores: { walk: 94, bike: 82, transit: 76, school: 80 },
    demographics: {
      population: '30,800',
      medianAge: 35,
      medianIncome: '$115K',
      medianRent: '$2,800',
    },
    schools: [
      { name: 'Francis Scott Key Elementary', grades: 'K–5', type: 'Public', rating: 8 },
      { name: 'Herbert Hoover Middle School', grades: '6–8', type: 'Public', rating: 7 },
      { name: 'Abraham Lincoln High School', grades: '9–12', type: 'Public', rating: 7 },
    ],
    restaurants: [
      { name: 'Outerlands', type: 'Californian', note: 'Rustic, seasonal brunch and dinner with legendary Dutch pancakes' },
      { name: 'Arizmendi Bakery', type: 'Bakery', note: 'Worker-owned cooperative bakery with outstanding breads and pizza' },
      { name: 'Gochi Japanese Tapas', type: 'Japanese', note: 'Inventive izakaya-style small plates in a neighborhood gem' },
      { name: 'Thanh Long', type: 'Vietnamese', note: 'Classic SF Vietnamese seafood spot since 1971' },
    ],
    thingsToDo: [
      { name: 'Golden Gate Park', type: 'Outdoors', note: 'Direct access to the park from the neighborhood\'s northern edge' },
      { name: 'UCSF Parnassus', type: 'Landmark', note: 'Hilltop medical campus with remarkable views over the city and ocean' },
      { name: 'Irving Street', type: 'Shopping', note: 'Walkable strip of local restaurants, coffee, and boutiques' },
      { name: 'Corona Heights', type: 'Outdoors', note: 'Rocky hilltop park with 360-degree views of the city' },
    ],
  },
  {
    name: 'NOPA',
    slug: 'nopa',
    region: 'sf',
    tagline: 'One of SF\'s most dynamic and walkable neighborhoods',
    description: "NOPA — North of the Panhandle — has transformed over the past two decades from a transitional neighborhood into one of the most desirable and vibrant areas in the city. The Panhandle, a linear park that stretches east from Golden Gate Park, provides a green buffer and beloved running/biking path. Divisadero Street is the main strip: a dense, walkable corridor of acclaimed restaurants, cocktail bars, coffee shops, and music venues that buzzes on any given evening. The housing stock is largely Victorian and Edwardian flats with beautiful original details. NOPA attracts food and design professionals, young families, and anyone who wants a fully walkable, culture-rich neighborhood at a slight discount to Pacific Heights.",
    vibe: ['Vibrant', 'Foodie', 'Walkable', 'Creative'],
    image: 'https://images.unsplash.com/photo-1767667536883-0e9dd308f7ef?auto=format&fit=crop&w=1200&q=80',
    market: {
      medianPrice: '$1.35M',
      pricePerSqft: '$1,050',
      avgDaysOnMarket: 18,
      updated: 'March 2026',
    },
    scores: { walk: 95, bike: 90, transit: 76, school: 65 },
    demographics: {
      population: '18,500',
      medianAge: 34,
      medianIncome: '$128K',
      medianRent: '$3,050',
    },
    schools: [
      { name: 'Alamo Elementary', grades: 'K–5', type: 'Public', rating: 6 },
      { name: 'Buena Vista Horace Mann', grades: 'K–8', type: 'Public', rating: 7 },
      { name: 'Gateway High School', grades: '9–12', type: 'Charter', rating: 7 },
    ],
    restaurants: [
      { name: 'Nopa', type: 'Californian', note: "SF's most influential neighborhood restaurant — open until 1am" },
      { name: 'Nopalito', type: 'Mexican', note: 'Organic, farm-to-table Mexican from the Nopa team' },
      { name: 'The Mill', type: 'Café', note: 'Celebrated bread and coffee from Josey Baker and Four Barrel' },
      { name: 'Ragazza', type: 'Italian', note: 'Neighborhood pizza and pasta on Divisadero, a local favorite' },
    ],
    thingsToDo: [
      { name: 'The Panhandle', type: 'Outdoors', note: 'Linear park perfect for running, biking, and weekend picnics' },
      { name: 'Divisadero Street', type: 'Shopping', note: 'Bars, boutiques, and one of SF\'s best restaurant corridors' },
      { name: 'Independent Music Venue', type: 'Culture', note: '1,000-capacity music hall hosting national and local acts' },
      { name: 'Alamo Square Park', type: 'Park', note: 'Iconic Painted Ladies view, close to NOPA\'s eastern edge' },
    ],
  },
  {
    name: 'Potrero Hill',
    slug: 'potrero-hill',
    region: 'sf',
    tagline: 'Sunny, creative, and quietly one of SF\'s most beloved neighborhoods',
    description: "Potrero Hill earns a devoted following because of what it quietly offers: exceptional sunshine (one of the warmest microclimates in the city), stunning views of downtown and the bay, a strong sense of community, and beautiful Victorians and newer homes on peaceful tree-lined streets. 18th Street provides a walkable commercial strip with good coffee, neighborhood restaurants, and local shops. The neighborhood borders the Dogpatch to the south and sits above SoMa to the east, making it well-connected to the tech and design corridor that runs through that part of the city. It's a neighborhood that feels found rather than discovered — residents tend to be passionate advocates for it.",
    vibe: ['Sunny', 'Community-Oriented', 'Residential', 'Creative'],
    image: 'https://images.unsplash.com/photo-1646376154861-ba480417c15f?auto=format&fit=crop&w=1200&q=80',
    market: {
      medianPrice: '$1.45M',
      pricePerSqft: '$1,000',
      avgDaysOnMarket: 11,
      updated: 'March 2026',
    },
    scores: { walk: 90, bike: 68, transit: 71, school: 70 },
    demographics: {
      population: '12,400',
      medianAge: 36,
      medianIncome: '$132K',
      medianRent: '$3,000',
    },
    schools: [
      { name: 'Daniel Webster Elementary', grades: 'K–5', type: 'Public', rating: 6 },
      { name: 'James Lick Middle School', grades: '6–8', type: 'Public', rating: 6 },
      { name: 'San Francisco Montessori', grades: 'K–6', type: 'Private', rating: 8 },
    ],
    restaurants: [
      { name: 'Plow', type: 'Brunch', note: 'One of SF\'s most beloved breakfast spots with lines to prove it' },
      { name: 'Piccino', type: 'Italian', note: 'Neighborhood Italian with a wood-fired oven and sunny patio' },
      { name: 'Goat Hill Pizza', type: 'Pizza', note: 'Neighborhood institution with Monday night all-you-can-eat pizza' },
      { name: 'Farley\'s', type: 'Café', note: 'Classic Potrero Hill coffee shop and community gathering place' },
    ],
    thingsToDo: [
      { name: 'Potrero Hill Recreation Center', type: 'Outdoors', note: 'Community rec center with views and athletic facilities' },
      { name: '18th Street', type: 'Shopping', note: 'Walkable strip of local cafés, restaurants, and boutiques' },
      { name: 'McKinley Square', type: 'Park', note: 'Hilltop park with panoramic bay and downtown views' },
      { name: 'Dogpatch Corridor', type: 'Culture', note: 'Adjacent design, dining, and gallery district along 3rd Street' },
    ],
  },
  {
    name: 'Bernal Heights',
    slug: 'bernal-heights',
    region: 'sf',
    tagline: 'A tight-knit community with hilltop charm and eclectic energy',
    description: "Bernal Heights is the kind of neighborhood that people move to once and never leave. Centered on a rocky hill with a beloved off-leash dog park at the summit, it has the character of a small town embedded within a major city. Cortland Avenue is the main commercial street — an authentic neighborhood corridor with bakeries, coffee shops, bookstores, and casual restaurants that serve the local community without trying to attract anyone else. The housing stock is a colorful mix of Victorian cottages, bungalows, and craftsman homes. Bernal has long been known for its progressive politics, community gardens, and neighborly culture. Families, long-term renters who became owners, and younger buyers looking for value and community all find a home here.",
    vibe: ['Community-Oriented', 'Eclectic', 'Family-Friendly', 'Walkable'],
    image: 'https://images.unsplash.com/photo-1704243439933-df9281fb0e80?auto=format&fit=crop&w=1200&q=80',
    market: {
      medianPrice: '$1.65M',
      pricePerSqft: '$1,050',
      avgDaysOnMarket: 20,
      updated: 'March 2026',
    },
    scores: { walk: 91, bike: 72, transit: 65, school: 73 },
    demographics: {
      population: '23,700',
      medianAge: 37,
      medianIncome: '$140K',
      medianRent: '$2,950',
    },
    schools: [
      { name: 'Cesar Chavez Elementary', grades: 'K–5', type: 'Public', rating: 4 },
      { name: 'Bernal Hill Elementary', grades: 'K–5', type: 'Public', rating: 7 },
      { name: 'Lick-Wilmerding High School', grades: '9–12', type: 'Private', rating: 9 },
    ],
    restaurants: [
      { name: 'Hillside Supper Club', type: 'Californian', note: 'Intimate neighborhood restaurant with a rotating seasonal menu' },
      { name: 'Good Good Culture Club', type: 'Pan-Asian', note: 'Inventive Asian-American cooking in a lively, colorful space' },
      { name: 'Sightglass Coffee', type: 'Café', note: 'Third-wave coffee shop on Cortland — a new neighborhood anchor' },
      { name: 'Bernal Bakery', type: 'Bakery', note: 'Neighborhood bakery known for sourdough and morning pastries' },
    ],
    thingsToDo: [
      { name: 'Bernal Hill Park', type: 'Outdoors', note: 'Off-leash hilltop park with 360-degree views of San Francisco' },
      { name: 'Cortland Avenue', type: 'Shopping', note: 'Authentic neighborhood commercial strip with local businesses' },
      { name: 'Holly Park', type: 'Park', note: 'Quiet oval park with picnic tables and mature trees' },
      { name: 'Precita Eyes Murals', type: 'Culture', note: 'Community mural arts center preserving Mission/Bernal street art' },
    ],
  },
  {
    name: 'Glen Park',
    slug: 'glen-park',
    region: 'sf',
    tagline: 'A quiet village feel with canyon trails and easy BART access',
    description: "Glen Park is one of San Francisco's best-kept secrets — a small, walkable village nestled in a valley in the southeastern part of the city. Diamond Street forms the compact commercial center: a handful of cafés, a bookstore, a wine bar, and neighborhood restaurants that have been there for years. Glen Canyon Park, a 70-acre natural reserve with hiking trails and a creek, provides a remarkable taste of wildness within the urban grid. The BART station makes the commute to downtown or the Peninsula efficient. Young families are increasingly drawn to Glen Park for its relative affordability, top elementary schools, and genuine neighborhood character.",
    vibe: ['Quiet', 'Family-Friendly', 'Nature-Oriented', 'Village-Like'],
    image: 'https://images.unsplash.com/photo-1776004517835-5a8a80cb7364?auto=format&fit=crop&w=1200&q=80',
    market: {
      medianPrice: '$2.7M',
      pricePerSqft: '$980',
      avgDaysOnMarket: 10,
      updated: 'March 2026',
    },
    scores: { walk: 82, bike: 54, transit: 66, school: 76 },
    demographics: {
      population: '8,100',
      medianAge: 38,
      medianIncome: '$148K',
      medianRent: '$3,100',
    },
    schools: [
      { name: 'Glen Park Elementary', grades: 'K–5', type: 'Public', rating: 8 },
      { name: 'Miraloma Elementary', grades: 'K–5', type: 'Public', rating: 8 },
      { name: 'Lick-Wilmerding High School', grades: '9–12', type: 'Private', rating: 9 },
    ],
    restaurants: [
      { name: 'Glen Park Station', type: 'American', note: 'Neighborhood gastropub with craft beers and hearty food' },
      { name: 'Gialina', type: 'Pizza', note: 'Acclaimed Neapolitan pizza on Diamond Street — often packed' },
      { name: 'Caffé Lungo', type: 'Café', note: 'Beloved morning spot for espresso, pastries, and neighborhood conversation' },
      { name: 'Canyon Market', type: 'Market', note: 'Community-owned market with excellent prepared foods and local products' },
    ],
    thingsToDo: [
      { name: 'Glen Canyon Park', type: 'Outdoors', note: '70-acre natural park with creek trails and wildflowers' },
      { name: 'Diamond Street', type: 'Shopping', note: 'Small-town commercial strip with independent businesses' },
      { name: 'Glen Park BART', type: 'Transit', note: 'Direct BART access to downtown SF and the Peninsula' },
      { name: 'Noe Valley Border Walks', type: 'Outdoors', note: 'Stairway walks connecting Glen Park to sunny Noe Valley' },
    ],
  },
  {
    name: 'Dogpatch',
    slug: 'dogpatch',
    region: 'sf',
    tagline: 'Industrial heritage, waterfront views, and a thriving creative scene',
    description: "Dogpatch has undergone one of San Francisco's most dramatic transformations over the past decade. Once an industrial waterfront district, it's now home to design studios, tech offices, acclaimed restaurants, and a growing residential base that values its warehouse loft spaces and newer condos. The 22nd Street corridor has emerged as one of the most dynamic in the city. The waterfront and Crane Cove Park offer outdoor space and bay views. The neighborhood is adjacent to UCSF's Mission Bay campus and within easy reach of Chase Center. Dogpatch retains a gritty, genuine quality that more established neighborhoods have lost — it's still in the process of becoming, and that energy is part of its appeal.",
    vibe: ['Creative', 'Industrial-Chic', 'Waterfront', 'Emerging'],
    image: 'https://images.unsplash.com/photo-1609625721733-fa92843f8c7f?auto=format&fit=crop&w=1200&q=80',
    market: {
      medianPrice: '$830K',
      pricePerSqft: '$950',
      avgDaysOnMarket: 29,
      updated: 'March 2026',
    },
    scores: { walk: 88, bike: 89, transit: 68, school: 62 },
    demographics: {
      population: '6,200',
      medianAge: 34,
      medianIncome: '$127K',
      medianRent: '$2,950',
    },
    schools: [
      { name: 'Daniel Webster Elementary', grades: 'K–5', type: 'Public', rating: 6 },
      { name: 'De Marillac Academy', grades: '5–8', type: 'Catholic', rating: 7 },
      { name: 'KIPP SF Bay Academy', grades: '5–8', type: 'Charter', rating: 7 },
    ],
    restaurants: [
      { name: 'Serpentine', type: 'American', note: 'Farm-to-table neighborhood restaurant in a converted warehouse' },
      { name: 'Café Umami', type: 'Café', note: 'Neighborhood coffee and lunch spot beloved by the local creative community' },
      { name: 'Hard Knox Cafe', type: 'Southern', note: 'Soul food and Southern cooking with a devoted local following' },
      { name: 'The Ramp', type: 'Seafood', note: 'Classic waterfront bar and seafood shack on the China Basin shoreline' },
    ],
    thingsToDo: [
      { name: 'Crane Cove Park', type: 'Outdoors', note: 'New waterfront park with bay swimming, picnicking, and views' },
      { name: 'Minnesota Street Project', type: 'Culture', note: 'Nonprofit complex of contemporary art galleries in Dogpatch' },
      { name: '22nd Street Corridor', type: 'Shopping', note: 'Design studios, restaurants, and shops on the main strip' },
      { name: 'Chase Center', type: 'Sports', note: 'Golden State Warriors arena and concert venue in adjacent Mission Bay' },
    ],
  },

  // ── Peninsula ────────────────────────────────────────────────────────────────
  {
    name: 'South San Francisco',
    slug: 'south-san-francisco',
    region: 'peninsula',
    tagline: 'The biotech capital of the world — suburban convenience at the gateway to the Peninsula',
    description: "South San Francisco has reinvented itself over the past three decades, transitioning from its industrial roots into the global epicenter of the biotechnology industry. Genentech, the company that pioneered the field, was founded here, and the sprawling biotech corridor along the bay continues to grow. The residential areas — particularly the hills above the commercial core — offer suburban comfort, good schools, and striking bay views at a price point meaningfully below San Francisco proper. The Caltrain station provides easy commuting access both north to SF and south to Silicon Valley. The downtown has been undergoing a revival, with new restaurants, shops, and public investments. For first-time buyers priced out of SF, South San Francisco offers genuine value.",
    vibe: ['Suburban', 'Family-Friendly', 'Well-Connected', 'Growing'],
    mapCenter: [37.6547, -122.4077],
    mapZoom: 13,
    subNeighborhoods: [
      {
        name: 'Old Town',
        coordinates: { lat: 37.6559, lng: -122.4069 },
        description: "SSF's historic core, now revitalized with independent restaurants, shops, and weekend events. Walking distance to Caltrain and retains the original neighborhood character.",
        vibe: ['Walkable', 'Historic', 'Revitalized'],
        priceRange: '$900K – $1.2M',
      },
      {
        name: 'Sunshine Gardens',
        coordinates: { lat: 37.6492, lng: -122.4141 },
        description: 'Mid-century neighborhood popular with families for its tree-lined streets, proximity to good schools, and well-maintained homes. A reliable first-time buyer area.',
        vibe: ['Family-Friendly', 'Quiet', 'Suburban'],
        priceRange: '$1.0M – $1.3M',
      },
      {
        name: 'Buri Buri',
        coordinates: { lat: 37.6478, lng: -122.4036 },
        description: "Elevated hilltop neighborhood with sweeping bay views and spacious lots. One of SSF's most desirable residential pockets, with larger single-family homes.",
        vibe: ['Bay Views', 'Spacious', 'Upscale'],
        priceRange: '$1.1M – $1.5M',
      },
      {
        name: 'Sign Hill',
        coordinates: { lat: 37.6549, lng: -122.4099 },
        description: 'Named for the famous hillside letters, this neighborhood offers panoramic views, a mix of housing types, and easy walking access to the revitalized downtown.',
        vibe: ['Views', 'Mixed Housing', 'Convenient'],
        priceRange: '$950K – $1.3M',
      },
      {
        name: 'Winston Manor',
        coordinates: { lat: 37.6417, lng: -122.4225 },
        description: 'Established post-war neighborhood with strong freeway and transit access. Popular with commuters heading to both SF and Silicon Valley.',
        vibe: ['Commuter-Friendly', 'Established', 'Well-Connected'],
        priceRange: '$900K – $1.15M',
      },
    ],
    image: 'https://images.unsplash.com/photo-1752465833701-223695a3a691?auto=format&fit=crop&w=1200&q=80',
    market: {
      medianPrice: '$1.28M',
      pricePerSqft: '$899',
      avgDaysOnMarket: 12,
      updated: 'March 2026',
    },
    scores: { walk: 67, bike: 49, transit: 43, school: 68 },
    demographics: {
      population: '67,200',
      medianAge: 36,
      medianIncome: '$98K',
      medianRent: '$2,450',
    },
    schools: [
      { name: 'Los Cerritos Elementary', grades: 'K–5', type: 'Public', rating: 6 },
      { name: 'Alta Loma Middle School', grades: '6–8', type: 'Public', rating: 6 },
      { name: 'South San Francisco High', grades: '9–12', type: 'Public', rating: 6 },
    ],
    restaurants: [
      { name: 'Lark Creek', type: 'American', note: 'Polished American cooking in a renovated historic building' },
      { name: 'Espetus Churrascaria', type: 'Brazilian', note: 'Brazilian steakhouse with tableside carved meats' },
      { name: 'Tanuki', type: 'Japanese', note: 'Local favorite for izakaya-style Japanese food and sake' },
      { name: 'Starbelly SSF', type: 'Café', note: 'Neighborhood café and brunch spot popular with biotech workers' },
    ],
    thingsToDo: [
      { name: 'Sign Hill Park', type: 'Outdoors', note: 'Hillside park with panoramic bay views and the famous SSF sign' },
      { name: 'Oyster Point Marina', type: 'Outdoors', note: 'Waterfront marina with trails, kayaking, and bay views' },
      { name: 'Downtown SSF', type: 'Shopping', note: 'Revitalized downtown with boutiques, restaurants, and weekend events' },
      { name: 'San Francisco Bay Trail', type: 'Outdoors', note: 'Multi-use bayfront trail connecting SSF to the broader Bay Area' },
    ],
  },
  {
    name: 'Daly City',
    slug: 'daly-city',
    region: 'peninsula',
    tagline: 'Diverse, affordable, and just minutes from San Francisco',
    description: "Daly City is the largest city in San Mateo County and one of the most diverse communities in the Bay Area, with a particularly strong Filipino-American population that has shaped the city's culture, food scene, and character for generations. Directly bordering San Francisco to the south, it offers buyers a genuine opportunity to live minutes from the city at a substantially lower price point. The BART station provides direct service to SF in under 20 minutes. The residential neighborhoods are primarily single-family homes with gardens, garages, and the suburban amenities that are hard to find within SF. Serramonte and Westlake are the most established neighborhoods. For many families, Daly City represents the best balance of proximity to SF and suburban livability.",
    vibe: ['Diverse', 'Suburban', 'Affordable', 'Well-Connected'],
    mapCenter: [37.6879, -122.4702],
    mapZoom: 13,
    subNeighborhoods: [
      {
        name: 'Westlake',
        coordinates: { lat: 37.6879, lng: -122.4702 },
        description: "One of the Bay Area's most iconic mid-century planned neighborhoods, developed by Henry Doelger in the late 1940s. Distinctive pastel stucco homes line curved streets — a slice of postwar architectural history with a devoted following.",
        vibe: ['Mid-Century', 'Historic', 'Character Homes'],
        priceRange: '$900K – $1.2M',
      },
      {
        name: 'Serramonte',
        coordinates: { lat: 37.6735, lng: -122.4700 },
        description: 'Southern Daly City anchored by Serramonte Center. Mix of single-family homes and condos with strong freeway access. Practical for buyers who prioritize convenience and value.',
        vibe: ['Convenient', 'Suburban', 'Good Value'],
        priceRange: '$850K – $1.1M',
      },
      {
        name: 'St. Francis Heights',
        coordinates: { lat: 37.7018, lng: -122.4618 },
        description: 'Hillside neighborhood along the SF border with bay views and consistent demand from SF buyers seeking more space. One of the most desirable pockets in Daly City.',
        vibe: ['Bay Views', 'SF Adjacent', 'Desirable'],
        priceRange: '$950K – $1.3M',
      },
      {
        name: 'Top of the Hill',
        coordinates: { lat: 37.7061, lng: -122.4710 },
        description: "The highest point in Daly City with panoramic bay and ocean views. Close to the Daly City BART station — one of the city's most sought-after locations.",
        vibe: ['Views', 'BART Access', 'Desirable'],
        priceRange: '$1.0M – $1.35M',
      },
      {
        name: 'Crocker',
        coordinates: { lat: 37.7099, lng: -122.4641 },
        description: 'Close to the SF border with easy Muni and BART access. Mix of older homes, updated condos, and multi-family. A practical option for SF commuters.',
        vibe: ['Transit-Friendly', 'SF Adjacent', 'Urban Edge'],
        priceRange: '$750K – $1.1M',
      },
    ],
    image: 'https://images.unsplash.com/photo-1653681127307-3989139316df?auto=format&fit=crop&w=1200&q=80',
    market: {
      medianPrice: '$1.21M',
      pricePerSqft: '$812',
      avgDaysOnMarket: 15,
      updated: 'March 2026',
    },
    scores: { walk: 77, bike: 52, transit: 57, school: 58 },
    demographics: {
      population: '107,000',
      medianAge: 37,
      medianIncome: '$89K',
      medianRent: '$2,200',
    },
    schools: [
      { name: 'Jefferson Elementary', grades: 'K–5', type: 'Public', rating: 5 },
      { name: 'Westlake Middle School', grades: '6–8', type: 'Public', rating: 6 },
      { name: 'Jefferson High School', grades: '9–12', type: 'Public', rating: 5 },
    ],
    restaurants: [
      { name: 'Max\'s Restaurant', type: 'Filipino-American', note: 'Classic Filipino comfort food — fried chicken is the signature' },
      { name: 'Tselogs', type: 'Filipino', note: 'Modern Filipino breakfast and brunch with Filipino staples' },
      { name: 'Kuya\'s Kitchen', type: 'Filipino', note: 'Homestyle Filipino cooking loved by the local community' },
      { name: 'Goldilocks Bakeshop', type: 'Bakery', note: 'Filipino bakery chain with cakes, breads, and savory pastries' },
    ],
    thingsToDo: [
      { name: 'Cow Palace', type: 'Entertainment', note: 'Historic multi-purpose arena hosting concerts and events' },
      { name: 'Gellert Park', type: 'Park', note: 'Community park with sports facilities and open space' },
      { name: 'Serramonte Shopping Center', type: 'Shopping', note: 'Major retail center with broad selection of stores' },
      { name: 'John Daly Blvd Dining', type: 'Food', note: 'Dense corridor of Filipino and Asian restaurants and markets' },
    ],
  },
  {
    name: 'Pacifica',
    slug: 'pacifica',
    region: 'peninsula',
    tagline: 'Coastal living, surf culture, and some of the most scenic scenery in the Bay Area',
    description: "Pacifica sits right where the Peninsula meets the Pacific Ocean — a small coastal city of about 40,000 that offers something genuinely rare in the Bay Area: affordable homes, direct beach access, and a genuine small-town feel just 15 miles from downtown San Francisco. The city is divided into distinct neighborhoods, each with its own character: Linda Mar in the south is the most suburban and family-friendly; Rockaway Beach in the middle offers hotels and restaurants; Sharp Park to the north sits closest to SF. The dramatic coastal bluffs, persistent fog, and powerful surf define the aesthetic and lifestyle here. For buyers who prioritize outdoor access, ocean proximity, and relative affordability, Pacifica is one of the best deals in the Bay Area.",
    vibe: ['Coastal', 'Outdoor-Focused', 'Quiet', 'Affordable'],
    mapCenter: [37.5987, -122.4900],
    mapZoom: 13,
    subNeighborhoods: [
      {
        name: 'Linda Mar',
        coordinates: { lat: 37.5807, lng: -122.4920 },
        description: "Pacifica's largest and most family-friendly neighborhood, built up through the 1950s–70s. Centers around Linda Mar Beach — one of the Bay Area's most popular surf spots. Best schools, most parks, and the most suburban feel in the city.",
        vibe: ['Family-Friendly', 'Surf Culture', 'Suburban'],
        priceRange: '$950K – $1.25M',
      },
      {
        name: 'Sharp Park',
        coordinates: { lat: 37.6018, lng: -122.4876 },
        description: 'Northernmost neighborhood, closest to SF. Home to the historic Sharp Park Golf Course (an Alister MacKenzie design). Mix of older homes and newer builds, with the most accessible entry-level pricing in Pacifica.',
        vibe: ['Golf Course Views', 'Convenient', 'Affordable'],
        priceRange: '$800K – $1.1M',
      },
      {
        name: 'Rockaway Beach',
        coordinates: { lat: 37.5931, lng: -122.4905 },
        description: 'Central Pacifica with the protected Rockaway Cove beach. More commercial character — hotels, restaurants, and the Pacifica Pier. Popular with weekend visitors and local families alike.',
        vibe: ['Beach Access', 'Dining', 'Mixed Use'],
        priceRange: '$900K – $1.2M',
      },
      {
        name: 'Manor',
        coordinates: { lat: 37.6107, lng: -122.4847 },
        description: 'One of the older parts of Pacifica with hillside character homes and a quiet residential feel. Close to the commercial strip but removed from beach crowds.',
        vibe: ['Quiet', 'Character Homes', 'Hillside'],
        priceRange: '$850K – $1.1M',
      },
      {
        name: 'Fairmont',
        coordinates: { lat: 37.6098, lng: -122.4920 },
        description: 'Elevated hillside neighborhood with some of the best ocean and coastal views in Pacifica. Larger lots, more architectural variety, and a secluded feel.',
        vibe: ['Ocean Views', 'Secluded', 'Elevated'],
        priceRange: '$900K – $1.2M',
      },
    ],
    image: 'https://plus.unsplash.com/premium_photo-1675721844756-98f8232b1588?auto=format&fit=crop&w=1200&q=80',
    market: {
      medianPrice: '$1.33M',
      pricePerSqft: '$875',
      avgDaysOnMarket: 9,
      updated: 'March 2026',
    },
    scores: { walk: 53, bike: 30, transit: 32, school: 65 },
    demographics: {
      population: '39,700',
      medianAge: 42,
      medianIncome: '$105K',
      medianRent: '$2,500',
    },
    schools: [
      { name: 'Linda Mar Elementary', grades: 'K–5', type: 'Public', rating: 7 },
      { name: 'Ingrid B. Lacy Middle School', grades: '6–8', type: 'Public', rating: 7 },
      { name: 'Terra Nova High School', grades: '9–12', type: 'Public', rating: 7 },
    ],
    restaurants: [
      { name: 'Nick\'s Rockaway', type: 'Seafood', note: 'Oceanfront seafood restaurant and sports bar with panoramic views' },
      { name: 'Taco Bell Cantina', type: 'Mexican', note: 'Yes, Pacifica\'s oceanfront location is genuinely iconic' },
      { name: 'Moonraker Restaurant', type: 'American', note: 'Classic oceanfront dining in Rockaway Beach' },
      { name: 'Pacific Mist', type: 'Café', note: 'Neighborhood café with local coffee and weekend brunch' },
    ],
    thingsToDo: [
      { name: 'Linda Mar Beach', type: 'Outdoors', note: 'Popular surf beach with consistent waves and lifeguards in summer' },
      { name: 'Mori Point', type: 'Hiking', note: 'GGNRA trail with dramatic coastal bluff views' },
      { name: 'Sweeney Ridge', type: 'Hiking', note: 'Historic ridge trail with 360-degree views — on a clear day, incredible' },
      { name: 'Rockaway Beach', type: 'Outdoors', note: 'Protected cove beach popular with families and kayakers' },
    ],
  },
  {
    name: 'San Bruno',
    slug: 'san-bruno',
    region: 'peninsula',
    tagline: 'Central Peninsula location with good schools and commuter convenience',
    description: "San Bruno occupies a strategic position in the center of the Peninsula — close to SFO, served by both BART and Caltrain, and within easy reach of San Francisco, Silicon Valley, and the coast. It's a solidly suburban city that offers good value relative to its neighbors: well-maintained single-family homes, excellent public schools, and a strong sense of community. The Crestmoor neighborhood in the hills offers some of the best views and housing on the Peninsula. The downtown has benefited from recent investment and redevelopment. YouTube's headquarters puts San Bruno on the tech industry map. For buyers who commute along the Peninsula corridor and want suburban comfort, good transit access, and relative affordability, San Bruno delivers.",
    vibe: ['Suburban', 'Family-Friendly', 'Well-Connected', 'Central'],
    mapCenter: [37.6270, -122.4150],
    mapZoom: 13,
    subNeighborhoods: [
      {
        name: 'Crestmoor',
        coordinates: { lat: 37.6229, lng: -122.4185 },
        description: "San Bruno's most prestigious neighborhood, perched in the hills with sweeping bay views. Larger lots, well-maintained homes, and a quiet, established character. Consistently the highest prices in the city.",
        vibe: ['Upscale', 'Bay Views', 'Established'],
        priceRange: '$1.3M – $1.8M',
      },
      {
        name: 'Portola Highlands',
        coordinates: { lat: 37.6180, lng: -122.4280 },
        description: 'Newer hilltop development with panoramic views of the bay and mountains. More modern homes than other parts of San Bruno, popular with tech workers at nearby YouTube HQ.',
        vibe: ['Modern Homes', 'Views', 'Tech-Friendly'],
        priceRange: '$1.2M – $1.6M',
      },
      {
        name: 'San Bruno Park',
        coordinates: { lat: 37.6281, lng: -122.4050 },
        description: 'Central neighborhood surrounding the original community park. Family-friendly with good school access and the feel of classic Peninsula suburbia.',
        vibe: ['Family-Friendly', 'Central', 'Parks'],
        priceRange: '$1.0M – $1.3M',
      },
      {
        name: 'Rollingwood',
        coordinates: { lat: 37.6329, lng: -122.4100 },
        description: 'Well-maintained planned neighborhood with curved streets and consistent housing stock. Popular with families for its quietness and proximity to top-rated schools.',
        vibe: ['Quiet', 'Planned', 'Family-Friendly'],
        priceRange: '$1.0M – $1.35M',
      },
    ],
    image: 'https://plus.unsplash.com/premium_photo-1682320427001-e7a016510197?auto=format&fit=crop&w=1200&q=80',
    market: {
      medianPrice: '$1.45M',
      pricePerSqft: '$982',
      avgDaysOnMarket: 11,
      updated: 'March 2026',
    },
    scores: { walk: 72, bike: 44, transit: 45, school: 64 },
    demographics: {
      population: '44,800',
      medianAge: 37,
      medianIncome: '$102K',
      medianRent: '$2,400',
    },
    schools: [
      { name: 'Allen Elementary', grades: 'K–5', type: 'Public', rating: 6 },
      { name: 'Parkside Intermediate', grades: '6–8', type: 'Public', rating: 6 },
      { name: 'Capuchino High School', grades: '9–12', type: 'Public', rating: 6 },
    ],
    restaurants: [
      { name: 'Sushi Yoshizumi', type: 'Japanese', note: 'Michelin-starred omakase sushi — one of the Bay Area\'s best' },
      { name: 'Il Fornaio', type: 'Italian', note: 'Classic Italian chain in an attractive location near downtown' },
      { name: 'San Bruno Mountain Pub', type: 'American', note: 'Neighborhood bar and grill with a loyal local following' },
      { name: 'Golden Gate Dim Sum', type: 'Chinese', note: 'Reliable Cantonese dim sum popular with local families' },
    ],
    thingsToDo: [
      { name: 'San Bruno Mountain', type: 'Outdoors', note: '2,326-foot peak with trails and unmatched bay views' },
      { name: 'The Shops at Tanforan', type: 'Shopping', note: 'Large indoor mall with diverse retail and dining options' },
      { name: 'Crestmoor Neighborhood', type: 'Sightseeing', note: 'Hilltop homes with sweeping views of the Bay and Peninsula' },
      { name: 'Commodore Park', type: 'Park', note: 'Community park and recreation center at the heart of San Bruno' },
    ],
  },
  {
    name: 'Millbrae',
    slug: 'millbrae',
    region: 'peninsula',
    tagline: 'Outstanding schools, excellent transit, and authentic dim sum culture',
    description: "Millbrae is one of the Peninsula's most desirable cities for families — particularly those with connections to the Asian-American community. The city is home to some of the best schools in San Mateo County, and the unique BART/Caltrain intermodal station makes it the best-connected transit hub on the Peninsula, with direct service to both SF and Silicon Valley. The Broadway corridor offers an impressive concentration of authentic Cantonese, Mandarin, and Vietnamese restaurants that draw diners from across the Bay Area. The housing stock skews toward well-maintained single-family homes on generous lots. Demand is consistently high, inventory is limited, and prices reflect the city's status as a top-tier Peninsula address.",
    vibe: ['Family-Friendly', 'Transit-Rich', 'Diverse', 'Upscale'],
    mapCenter: [37.5985, -122.3975],
    mapZoom: 14,
    subNeighborhoods: [
      {
        name: 'Downtown Millbrae',
        coordinates: { lat: 37.5990, lng: -122.3870 },
        description: "Walkable core centered on Broadway near the BART/Caltrain intermodal station — one of the only spots on the Peninsula with both rail lines. Mix of condos, renovated homes, and an acclaimed restaurant corridor.",
        vibe: ['Walkable', 'Transit Hub', 'Dining'],
        priceRange: '$1.0M – $1.6M',
      },
      {
        name: 'Green Hills',
        coordinates: { lat: 37.5939, lng: -122.4020 },
        description: "Millbrae's most prestigious hillside neighborhood with larger lots, mature landscaping, and sweeping bay views. Adjacent to the Green Hills Country Club. Consistently the highest demand and prices in the city.",
        vibe: ['Upscale', 'Bay Views', 'Spacious'],
        priceRange: '$2.0M – $3.5M',
      },
      {
        name: 'Millbrae Meadows',
        coordinates: { lat: 37.6017, lng: -122.3922 },
        description: 'Flat, centrally-located neighborhood convenient to shopping and SFO. Smaller lots and more modest homes — the most accessible entry point into Millbrae.',
        vibe: ['Convenient', 'Accessible', 'Flat'],
        priceRange: '$1.1M – $1.5M',
      },
      {
        name: 'Spring Hill',
        coordinates: { lat: 37.5955, lng: -122.3980 },
        description: 'Mid-hills neighborhood with a mix of housing types and good freeway access. Quieter than downtown but close to amenities. Popular with families.',
        vibe: ['Quiet', 'Residential', 'Mid-Range'],
        priceRange: '$1.4M – $2.0M',
      },
    ],
    image: 'https://images.unsplash.com/photo-1666345136257-5132787afeac?auto=format&fit=crop&w=1200&q=80',
    market: {
      medianPrice: '$2.2M',
      pricePerSqft: '$1,200',
      avgDaysOnMarket: 12,
      updated: 'March 2026',
    },
    scores: { walk: 79, bike: 46, transit: 52, school: 88 },
    demographics: {
      population: '23,500',
      medianAge: 41,
      medianIncome: '$130K',
      medianRent: '$2,800',
    },
    schools: [
      { name: 'Green Hills Elementary', grades: 'K–5', type: 'Public', rating: 8 },
      { name: 'Taylor Middle School', grades: '6–8', type: 'Public', rating: 8 },
      { name: 'Mills High School', grades: '9–12', type: 'Public', rating: 8 },
    ],
    restaurants: [
      { name: 'Hong Kong Flower Lounge', type: 'Chinese', note: 'Legendary Cantonese seafood and dim sum — a Peninsula institution' },
      { name: 'Koi Palace', type: 'Chinese', note: 'Top-tier Cantonese seafood with a stunning live fish display' },
      { name: 'Spiedo', type: 'Italian', note: 'Northern Italian restaurant in the Westin hotel, well-regarded' },
      { name: 'Noodle King', type: 'Chinese', note: 'Casual noodle spot with excellent hand-pulled noodles and soups' },
    ],
    thingsToDo: [
      { name: 'Millbrae BART/Caltrain Station', type: 'Transit', note: 'The only station where BART and Caltrain intersect on the Peninsula' },
      { name: 'Central Park Millbrae', type: 'Park', note: 'Large community park with sports facilities and walking paths' },
      { name: 'Broadway Dining District', type: 'Food', note: 'Dense corridor of Cantonese, Mandarin, and Asian restaurants' },
      { name: 'Millbrae Meadows', type: 'Outdoors', note: 'Open space preserve with walking trails and bay views' },
    ],
  },
  {
    name: 'Burlingame',
    slug: 'burlingame',
    region: 'peninsula',
    tagline: 'The jewel of the Peninsula — charming, walkable, and exceptional in every way',
    description: "Burlingame is consistently ranked among the best places to live in California, and it earns that distinction through a combination of factors that are rarely found together: a beautiful, walkable downtown, top-rated public schools, tree-lined residential streets, convenient freeway and Caltrain access, and a genuine sense of community. Broadway and Burlingame Avenue are two of the Peninsula's finest commercial streets, offering independent restaurants, wine bars, boutiques, and weekend farmers markets. The housing stock is predominantly single-family homes on generous lots, with architectural styles ranging from Craftsman bungalows to Spanish colonials to post-war ranch homes. Proximity to SFO is an advantage for frequent travelers. Burlingame is not cheap — it commands a premium — but buyers who invest here rarely regret it.",
    vibe: ['Charming', 'Walkable', 'Upscale', 'Family-Friendly'],
    mapCenter: [37.5810, -122.3600],
    mapZoom: 14,
    subNeighborhoods: [
      {
        name: 'Burlingame Park',
        coordinates: { lat: 37.5784, lng: -122.3600 },
        description: "One of the oldest and most architecturally distinguished neighborhoods on the Peninsula. Tree-lined streets with Craftsman, Tudor, and Colonial homes dating to the early 1900s. Walking distance to downtown Broadway.",
        vibe: ['Historic', 'Character Homes', 'Walkable'],
        priceRange: '$2.2M – $4.0M',
      },
      {
        name: 'Easton Addition',
        coordinates: { lat: 37.5804, lng: -122.3640 },
        description: 'Highly sought-after neighborhood east of El Camino with top-ranked elementary schools and beautifully maintained homes. One of the most competitive real estate markets in San Mateo County.',
        vibe: ['Top Schools', 'Prestigious', 'Competitive'],
        priceRange: '$2.5M – $4.5M',
      },
      {
        name: 'Ray Park',
        coordinates: { lat: 37.5850, lng: -122.3640 },
        description: 'Established neighborhood near Ray Park and its playing fields with a mix of architectural styles and a family-friendly atmosphere. Well-located and consistently in demand.',
        vibe: ['Family-Friendly', 'Parks', 'Established'],
        priceRange: '$2.0M – $3.2M',
      },
      {
        name: 'Broadway',
        coordinates: { lat: 37.5820, lng: -122.3550 },
        description: "The Broadway corridor and surrounding blocks — walkable to Burlingame's acclaimed restaurant and retail scene. Mix of condos, townhomes, and single-family homes ideal for buyers who want walkability.",
        vibe: ['Walkable', 'Dining Scene', 'Convenient'],
        priceRange: '$1.5M – $2.8M',
      },
    ],
    image: 'https://images.unsplash.com/photo-1648937391328-675141e2b6e1?auto=format&fit=crop&w=1200&q=80',
    market: {
      medianPrice: '$3.0M',
      pricePerSqft: '$1,491',
      avgDaysOnMarket: 12,
      updated: 'March 2026',
    },
    scores: { walk: 84, bike: 56, transit: 45, school: 91 },
    demographics: {
      population: '31,300',
      medianAge: 41,
      medianIncome: '$155K',
      medianRent: '$3,100',
    },
    schools: [
      { name: 'Roosevelt Elementary', grades: 'K–5', type: 'Public', rating: 9 },
      { name: 'Burlingame Intermediate', grades: '6–8', type: 'Public', rating: 9 },
      { name: 'Burlingame High School', grades: '9–12', type: 'Public', rating: 9 },
    ],
    restaurants: [
      { name: 'Ramen Nagi', type: 'Japanese', note: 'Legendary ramen from the acclaimed Japanese chain' },
      { name: 'Village Pub', type: 'American', note: 'Michelin-starred gastropub in nearby Woodside, worth the short drive' },
      { name: 'Crush Wine Bar', type: 'Wine Bar', note: 'Neighborhood wine bar with excellent selection and small plates' },
      { name: 'Limon', type: 'Peruvian', note: 'Outstanding Peruvian-Californian cuisine on Broadway' },
    ],
    thingsToDo: [
      { name: 'Burlingame Avenue', type: 'Shopping', note: 'Charming pedestrian-friendly shopping and dining street' },
      { name: 'Washington Park', type: 'Park', note: 'Beautiful central park with tennis courts and weekend events' },
      { name: 'Coyote Point Recreation Area', type: 'Outdoors', note: 'Bay shoreline park with sailboarding, picnics, and bay views' },
      { name: 'Broadway Farmers Market', type: 'Food', note: 'Saturday farmers market with local produce and artisan goods' },
    ],
  },
  {
    name: 'San Mateo',
    slug: 'san-mateo',
    region: 'peninsula',
    tagline: 'Central Peninsula hub with a diverse, thriving downtown and excellent value',
    description: "San Mateo is the commercial and cultural hub of the Peninsula — a mid-size city with the services of a large one and the character of a smaller one. The downtown is genuinely excellent: a walkable grid of restaurants, bars, boutiques, and services that supports daily life without requiring a car. 4th Avenue and B Street form the restaurant corridor, with a notable concentration of Japanese and other Asian restaurants reflecting the city's diversity. The neighborhoods range from older established areas near the bay to the hillier, more exclusive areas approaching Hillsborough. Caltrain service is frequent and fast. San Mateo offers perhaps the best balance of value, amenities, and location of any city on the Peninsula — neither the most expensive nor the cheapest, but arguably the most complete.",
    vibe: ['Diverse', 'Vibrant', 'Central', 'Walkable'],
    mapCenter: [37.5550, -122.3230],
    mapZoom: 13,
    subNeighborhoods: [
      {
        name: 'Baywood',
        coordinates: { lat: 37.5429, lng: -122.3290 },
        description: "One of San Mateo's most prestigious and private neighborhoods with custom homes, large lots, and a secluded hillside setting approaching Hillsborough. Very low turnover — when homes come to market, they move quickly.",
        vibe: ['Prestigious', 'Hillside', 'Large Lots'],
        priceRange: '$2.5M – $5.0M+',
      },
      {
        name: 'Bay Meadows',
        coordinates: { lat: 37.5582, lng: -122.3011 },
        description: 'Newer master-planned community built on the site of the former Bay Meadows Racetrack. Modern homes, condos, and a walkable neighborhood center. Popular with younger buyers and tech workers.',
        vibe: ['Modern', 'Walkable', 'New Development'],
        priceRange: '$1.3M – $2.2M',
      },
      {
        name: 'Fiesta Gardens',
        coordinates: { lat: 37.5548, lng: -122.3038 },
        description: 'Centrally located family neighborhood with well-maintained homes and good school proximity. Consistent demand from families looking for a stable, established community at a reasonable entry point.',
        vibe: ['Family-Friendly', 'Central', 'Established'],
        priceRange: '$1.4M – $1.9M',
      },
      {
        name: 'The Highlands',
        coordinates: { lat: 37.5470, lng: -122.3250 },
        description: 'Hillside neighborhood on the western edge of San Mateo with larger homes and expansive views toward the bay. More exclusive character with a mix of custom and production homes.',
        vibe: ['Views', 'Upscale', 'Hillside'],
        priceRange: '$1.8M – $3.0M',
      },
      {
        name: 'North Central',
        coordinates: { lat: 37.5635, lng: -122.3204 },
        description: 'Walkable neighborhood close to downtown San Mateo, Caltrain, and Central Park. Mix of Craftsman homes and condos. Popular with commuters and buyers who want an urban lifestyle.',
        vibe: ['Walkable', 'Downtown Adjacent', 'Transit'],
        priceRange: '$1.2M – $1.8M',
      },
    ],
    image: 'https://images.unsplash.com/photo-1617141372324-a37d164e08ae?auto=format&fit=crop&w=1200&q=80',
    market: {
      medianPrice: '$2.15M',
      pricePerSqft: '$1,244',
      avgDaysOnMarket: 14,
      updated: 'March 2026',
    },
    scores: { walk: 80, bike: 58, transit: 48, school: 80 },
    demographics: {
      population: '106,000',
      medianAge: 37,
      medianIncome: '$118K',
      medianRent: '$2,900',
    },
    schools: [
      { name: 'Baywood Elementary', grades: 'K–5', type: 'Public', rating: 8 },
      { name: 'Abbott Middle School', grades: '6–8', type: 'Public', rating: 8 },
      { name: 'San Mateo High School', grades: '9–12', type: 'Public', rating: 7 },
    ],
    restaurants: [
      { name: 'Wakuriya', type: 'Japanese', note: 'Michelin-starred kaiseki restaurant — one of the Bay Area\'s finest' },
      { name: 'Ramen Dojo', type: 'Japanese', note: 'Long-running ramen shop with a devoted local following' },
      { name: 'Pausa', type: 'Italian', note: 'Acclaimed Italian restaurant with excellent pasta and wine' },
      { name: 'Flea St. Café', type: 'Californian', note: 'Pioneer of farm-to-table cuisine on the Peninsula since 1980' },
    ],
    thingsToDo: [
      { name: 'Central Park San Mateo', type: 'Park', note: 'Expansive park with Japanese garden, tennis, and duck pond' },
      { name: 'Downtown San Mateo', type: 'Shopping', note: '4th Ave and B Street restaurant and boutique corridor' },
      { name: 'San Mateo Japanese Garden', type: 'Culture', note: 'Serene Japanese garden inside Central Park, free to visit' },
      { name: 'Bay Meadows District', type: 'Shopping', note: 'New mixed-use development with shops, restaurants, and parks' },
    ],
  },
  {
    name: 'Foster City',
    slug: 'foster-city',
    region: 'peninsula',
    tagline: 'A planned waterfront community with excellent schools and bay access',
    description: "Foster City is a planned community built on filled tidal marshland in the 1960s, and it's aged remarkably well. The city's defining feature is its network of lagoons — miles of waterways that wind through residential neighborhoods, offering residents direct access to kayaking, paddleboarding, and sailing from their back yards. The schools are among the best in San Mateo County, which drives consistent demand from families. Major tech employers including Gilead Sciences and Electronic Arts are headquartered here. The housing stock is primarily single-family homes, townhouses, and condos in well-maintained planned developments. The city has a quiet, almost resort-like quality that appeals to those who want suburban comfort with a distinctive waterfront character.",
    vibe: ['Waterfront', 'Family-Friendly', 'Planned', 'Quiet'],
    mapCenter: [37.5600, -122.2660],
    mapZoom: 14,
    subNeighborhoods: [
      {
        name: 'Edgewater Isle',
        coordinates: { lat: 37.5601, lng: -122.2620 },
        description: "One of Foster City's most distinctive communities — homes line the lagoon waterfront with private dock access. Kayaking and paddleboarding from your back yard. Among the highest prices in the city.",
        vibe: ['Waterfront', 'Dock Access', 'Exclusive'],
        priceRange: '$1.8M – $3.5M',
      },
      {
        name: 'Isle Cove',
        coordinates: { lat: 37.5629, lng: -122.2659 },
        description: 'Planned waterfront community with homes along the inner lagoon channels. Water views from many units, a strong community feel, and easy access to parks and the Bay Trail.',
        vibe: ['Lagoon Views', 'Community', 'Outdoor-Focused'],
        priceRange: '$1.5M – $2.2M',
      },
      {
        name: 'Sea Cove',
        coordinates: { lat: 37.5660, lng: -122.2680 },
        description: 'Newer waterfront development with modern homes and condos near the bay. Popular with families for its proximity to top-rated schools and waterfront amenities.',
        vibe: ['Modern', 'Bay Adjacent', 'Family-Friendly'],
        priceRange: '$1.4M – $2.0M',
      },
      {
        name: 'Metro Center',
        coordinates: { lat: 37.5548, lng: -122.2680 },
        description: 'The most urban part of Foster City with condos, apartments, and proximity to major employers including Gilead and EA. Best walkability in the city and the easiest commute access.',
        vibe: ['Urban', 'Commuter-Friendly', 'Condos'],
        priceRange: '$900K – $1.4M',
      },
    ],
    image: 'https://images.unsplash.com/photo-1570822656575-de2257ee7373?auto=format&fit=crop&w=1200&q=80',
    market: {
      medianPrice: '$2.2M',
      pricePerSqft: '$1,124',
      avgDaysOnMarket: 13,
      updated: 'March 2026',
    },
    scores: { walk: 56, bike: 62, transit: 29, school: 85 },
    demographics: {
      population: '33,100',
      medianAge: 39,
      medianIncome: '$138K',
      medianRent: '$3,000',
    },
    schools: [
      { name: 'Foster City Elementary', grades: 'K–5', type: 'Public', rating: 9 },
      { name: 'Bowditch Middle School', grades: '6–8', type: 'Public', rating: 9 },
      { name: 'San Mateo High School', grades: '9–12', type: 'Public', rating: 7 },
    ],
    restaurants: [
      { name: 'Dragon Beaux', type: 'Chinese', note: 'Modern Cantonese seafood and dim sum — a Peninsula favorite' },
      { name: 'Spiedo', type: 'Italian', note: 'Northern Italian in a welcoming waterfront-adjacent setting' },
      { name: 'Taiko', type: 'Japanese', note: 'Beloved Japanese comfort food with a loyal local following' },
      { name: 'Ikes Love & Sandwiches', type: 'Sandwiches', note: 'Bay Area chain with creative, overstuffed sandwiches' },
    ],
    thingsToDo: [
      { name: 'Foster City Lagoons', type: 'Outdoors', note: 'Miles of navigable waterways for kayaking and paddleboarding' },
      { name: 'Leo J. Ryan Memorial Park', type: 'Park', note: 'Bayfront park with picnic areas, sports fields, and water access' },
      { name: 'Bay Trail', type: 'Outdoors', note: 'Scenic multi-use path along the bay with views of the East Bay hills' },
      { name: 'Edgewater Sports Complex', type: 'Sports', note: 'Community sports and recreation hub' },
    ],
  },
];
